/**
 * /news/:slug no tema do Cabal Next (getNewsBySlug; NewsDetailPage.tsx antiga fica no projeto): capa com moldura,
 * etiquetas, titulo, subtitulo e o HTML da noticia (cadastrado no painel) com o estilo do Next, + "Mais noticias"
 * embaixo. Link antigo /news/<id numerico> ainda acha a noticia e a URL vira /news/<slug> (dono 2026-10-22: o id nao
 * fica publico).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Newspaper } from 'lucide-react';
import { getNewsBySlug, type NewsItem } from '@/shared/services/eventsService';
import { NextFooter, NextHeader, useReveal, useScrollTopOnMount } from '@/features/Home/next/NextLayout';
import {
  CATEGORY_LABEL,
  NEWS_ASSET,
  newsCategoryOf,
  newsDate,
  newsPlainTitle,
  useNewsCards,
} from '@/features/Home/next/newsData';
import { newsImg, newsImgFallback } from '@/shared/utils/newsImg';
import { NewsCardItem } from './NextNewsPage';
import '@/features/Home/next/news.css';
import './news-pages.css';

export default function NextNewsDetailPage() {
  const { id: slug } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [item, setItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { cards } = useNewsCards();
  useScrollTopOnMount();

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getNewsBySlug(slug)
      .then((r) => {
        if (cancelled) return;
        setItem(r);
        if (r.slug && r.slug !== slug) navigate(`/news/${r.slug}`, { replace: true });   // link antigo (id)
      })
      .catch((e: { Message?: string; message?: string }) => {
        if (!cancelled) setError(e?.Message || e?.message || 'Notícia não encontrada.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  useEffect(() => {
    document.title = item ? `${newsPlainTitle(item.title)} · Cabal Next` : 'Notícias · Cabal Next';
  }, [item]);

  const category = item ? newsCategoryOf(item.title) : 'noticia';
  const current = item?.slug ?? slug;
  const more = useMemo(() => cards.filter((c) => !c.slug || c.slug !== current).slice(0, 3), [cards, current]);
  useReveal(rootRef, [loading, more.length]);

  return (
    <div ref={rootRef} className="nx">
      <NextHeader active="noticias" />
      <main>
        <section className="nxp-hero nxp-hero--slim" style={{ backgroundImage: `url(${NEWS_ASSET}/news-bg.webp)` }}>
          <div className="nx-container">
            <Link className="nxp-back" to="/news">
              <ArrowLeft size={16} /> Voltar às notícias
            </Link>
          </div>
        </section>

        <section className="nxp-body">
          <div className="nx-container">
            {loading && (
              <div className="nxp-loading">
                <Loader2 size={30} className="nxp-spin" />
              </div>
            )}

            {!loading && error && (
              <div className="nxp-error" data-reveal>
                <Newspaper size={38} />
                <h1>{error}</h1>
                <p>Talvez ela tenha sido removida ou o link esteja incorreto.</p>
                <Link className="nxp-more" to="/news">
                  Ver todas as notícias
                </Link>
              </div>
            )}

            {!loading && !error && item && (
              <article className="nxp-article" data-reveal>
                {item.image && (
                  <div className="nxp-article__cover">
                    <img src={newsImg(item.image, 1280)} onError={newsImgFallback(item.image)} alt={item.title} />
                  </div>
                )}
                <div className="nxp-article__body">
                  <span className="nxn-meta">
                    <span className={`nxn-chip nxn-chip--${category}`}>{CATEGORY_LABEL[category]}</span>
                    <span className="nxn-chip nxn-chip--date">{newsDate(item.createdDate)}</span>
                  </span>
                  <h1>{newsPlainTitle(item.title)}</h1>
                  {item.subTitle && <p className="nxp-article__sub">{item.subTitle}</p>}
                  <div className="nxp-html" dangerouslySetInnerHTML={{ __html: item.html || '' }} />
                </div>
              </article>
            )}

            {!loading && more.length > 0 && (
              <div className="nxp-related">
                <h2 className="nxp-related__title" data-reveal>
                  Mais notícias
                </h2>
                <div className="nxp-grid">
                  {more.map((c, i) => (
                    <NewsCardItem key={c.slug ?? `fb-${i}`} card={c} delay={i * 0.08} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <NextFooter />
    </div>
  );
}
